import axiosInstance from '../../services/axiosInstance';
import type {
  ProductReferenceImpactPreflightResponse,
  RecipeReferenceImpactPreflightResponse,
} from './types';

function isDuplicateCollectionNameError(error: any): boolean {
  const message = String(error?.response?.data?.message || error?.message || '').toLowerCase();
  return message.includes('already exists') || message.includes('redan');
}

async function createCollection(name: string): Promise<string> {
  const response = await axiosInstance.post('/api/collections', {
    name,
    description: '',
  });
  const collectionId = String(response?.data?._id || '').trim();
  if (!collectionId) {
    throw new Error('Kunde inte identifiera den skapade samlingen.');
  }
  return collectionId;
}

async function addAssetsToCollection(collectionId: string, assetIds: string[]): Promise<void> {
  if (assetIds.length <= 1) {
    await axiosInstance.post(`/api/collections/${collectionId}/assets`, {
      assetId: assetIds[0],
    });
    return;
  }

  await axiosInstance.post(`/api/collections/${collectionId}/assets/bulk`, {
    assetIds,
  });
}

export async function fetchRecipeReferenceImpactPreflight(
  assetIds: string[],
): Promise<RecipeReferenceImpactPreflightResponse> {
  const { data } = await axiosInstance.post('/api/reference-impact/recipes/preflight', {
    assetIds,
  });
  return data;
}

export async function fetchProductReferenceImpactPreflight(
  assetIds: string[],
): Promise<ProductReferenceImpactPreflightResponse> {
  const { data } = await axiosInstance.post('/api/reference-impact/products/preflight', {
    assetIds,
  });
  return data;
}

export async function createImpactCollection(
  name: string,
  assetIds: string[],
): Promise<{ collectionId: string; collectionName: string }> {
  const trimmedName = String(name || '').trim();
  if (!trimmedName) {
    throw new Error('Samlingsnamn saknas.');
  }
  if (!Array.isArray(assetIds) || assetIds.length === 0) {
    throw new Error('Det finns inga assets att lägga i samlingen.');
  }

  let collectionName = trimmedName;
  let collectionId: string;

  try {
    collectionId = await createCollection(collectionName);
  } catch (error: any) {
    if (!isDuplicateCollectionNameError(error)) {
      throw error;
    }
    collectionName = `${trimmedName} (${Date.now()})`;
    collectionId = await createCollection(collectionName);
  }

  await addAssetsToCollection(collectionId, assetIds);

  return {
    collectionId,
    collectionName,
  };
}
